import {useCallback, useMemo} from 'react';
import {LocalStorage} from '@chekin/core';

type StoredFlag = {id?: string; showed?: boolean};

type UseModalOncePerSessionProps = {
  id?: string;
  storageKey: string;
  isReady: boolean;
};

const getModalOncePerSessionKey = (storageKey: string, id: string) =>
  `${storageKey}-${id}`;

const isModalShown = (storageKey: string, id?: string) => {
  if (!id) return false;

  const stored = LocalStorage.get<StoredFlag>(getModalOncePerSessionKey(storageKey, id));

  return stored?.id === id && Boolean(stored?.showed);
};

const markModalAsShown = (storageKey: string, id?: string) => {
  if (!id) return;

  LocalStorage.set(getModalOncePerSessionKey(storageKey, id), {
    id,
    showed: true,
  } satisfies StoredFlag);
};

const clearModalShown = (storageKey: string, id?: string) => {
  if (!id) return;

  LocalStorage.remove(getModalOncePerSessionKey(storageKey, id));
};

function useModalOncePerSession({id, storageKey, isReady}: UseModalOncePerSessionProps) {
  const shouldOpenModal = useMemo(() => {
    if (!id || !isReady) return false;

    return !isModalShown(storageKey, id);
  }, [storageKey, id, isReady]);

  const markAsShown = useCallback(() => {
    markModalAsShown(storageKey, id);
  }, [storageKey, id]);

  return {shouldOpenModal, markAsShown};
}

export {useModalOncePerSession, isModalShown, markModalAsShown, clearModalShown};
